// /app/frontend/src/context/PermissionContext.js
// Permission context for role-based UI access control

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const PermissionContext = createContext(null);

// Permission levels hierarchy
const PERMISSION_LEVELS = ['none', 'view', 'own', 'edit', 'full'];

export function PermissionProvider({ children }) {
  const { token: authToken } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const fetchPermissions = useCallback(async () => {
    const token = authToken || localStorage.getItem('apollo_token');
    if (!token) {
      setLoading(false);
      setPermissions({});
      setRole(null);
      setIsAdmin(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/api/admin/my-permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPermissions(response.data.permissions || {});
      setRole(response.data.role);
      setIsAdmin(response.data.is_admin || false);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      // If unauthorized, clear permissions
      if (error.response?.status === 401) {
        setPermissions({});
        setRole(null);
        setIsAdmin(false);
      }
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    setLoading(true);
    fetchPermissions();
    
    // Listen for storage changes (e.g., login in another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'apollo_token') {
        setLoading(true);
        fetchPermissions();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [fetchPermissions]);

  // Check if user has required permission level for a module
  const hasPermission = useCallback((module, requiredLevel = 'view') => {
    // Admins have full access
    if (isAdmin && !role) return true;
    
    const userLevel = permissions[module] || 'none';
    
    // Full always grants access
    if (userLevel === 'full') return true;
    
    // Handle special permission values
    if (userLevel === 'own' && ['view', 'own'].includes(requiredLevel)) return true;
    if (userLevel === 'sales' && ['view', 'own'].includes(requiredLevel)) return true;
    if (userLevel === 'production' && ['view', 'own'].includes(requiredLevel)) return true;
    
    // Compare permission levels
    const userIndex = PERMISSION_LEVELS.indexOf(userLevel);
    const requiredIndex = PERMISSION_LEVELS.indexOf(requiredLevel);
    
    return userIndex >= requiredIndex;
  }, [permissions, isAdmin, role]);

  // Check if user can perform specific action
  const canView = useCallback((module) => (module === 'super_admin_only'
    ? (isAdmin && !role) || (role?.id || role?.role_id || role) === 'role_super_admin'
    : module === 'full_admin_only'
      ? (isAdmin && !role) || ['role_super_admin', 'role_admin'].includes(role?.id || role?.role_id || role)
      : hasPermission(module, 'view')), [hasPermission, isAdmin, role]);
  const canEdit = useCallback((module) => hasPermission(module, 'edit'), [hasPermission]);
  const canCreate = useCallback((module) => hasPermission(module, 'full'), [hasPermission]);
  const canDelete = useCallback((module) => hasPermission(module, 'full'), [hasPermission]);

  // Get permission level for a module
  const getPermissionLevel = useCallback((module) => {
    if (isAdmin && !role) return 'full';
    return permissions[module] || 'none';
  }, [permissions, isAdmin, role]);

  // Refresh permissions (call after role change)
  const refreshPermissions = useCallback(() => {
    setLoading(true);
    fetchPermissions();
  }, [fetchPermissions]);

  const value = {
    permissions,
    role,
    isAdmin,
    loading,
    hasPermission,
    canView,
    canEdit,
    canCreate,
    canDelete,
    getPermissionLevel,
    refreshPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}

// Higher-order component for permission-based rendering
export function withPermission(WrappedComponent, module, requiredLevel = 'view') {
  return function PermissionWrapper(props) {
    const { hasPermission, loading } = usePermissions();
    
    if (loading) return null;
    if (!hasPermission(module, requiredLevel)) return null;
    
    return <WrappedComponent {...props} />;
  };
}

// Component for conditional rendering based on permission
export function PermissionGate({ module, level = 'view', fallback = null, children }) {
  const { hasPermission, loading } = usePermissions();
  
  if (loading) return null;
  if (!hasPermission(module, level)) return fallback;
  
  return children;
}

export default PermissionContext;
